import {
  breadcrumbLd,
  faqLd,
  itemListLd,
  speakableLd,
  tableDatasetLd,
  SITE_OG_IMAGE,
  pageTitle,
} from "@/lib/seo";
import type { TopicDef } from "@/lib/topics-config";
import type { TopicIndexData } from "@/lib/topics.server";
import { topicFaqs } from "@/lib/topic-stats";
import { isTableStateFiltered, type TableSearch } from "@/lib/table-state";


const SITE = "https://autospecdata.com";
const OG = SITE_OG_IMAGE;

function clamp(text: string, max = 158) {
  return text.length <= max ? text : `${text.slice(0, max - 1).replace(/\s+\S*$/, "")}…`;
}

/**
 * Shared head() payload for every topic index route. `state` carries the
 * table's URL sort/page params: those permutations stay crawlable but
 * noindex, and always canonicalise back to the clean topic URL.
 */
export function buildTopicHead(
  def: TopicDef,
  data: TopicIndexData | null,
  answer: string,
  state: TableSearch = {},
) {
  const url = `${SITE}${def.path}`;
  if (!data || data.entries.length === 0) {
    return {
      meta: [
        { title: pageTitle(def.title) },
        { name: "robots", content: "noindex, follow" },
      ],
    };
  }
  const description = clamp(answer);
  const stated = isTableStateFiltered(state);
  return {
    meta: [
      { title: pageTitle(def.title) },
      { name: "description", content: description },
      { property: "og:title", content: def.title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG },
      { name: "twitter:image", content: OG },
      ...(stated ? [{ name: "robots", content: "noindex, follow" }] : []),
    ],
    links: [{ rel: "canonical", href: url }],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          itemListLd(
            def.h1,
            data.entries.slice(0, 100).map((e) => ({
              name: `${e.makeName} ${e.modelName}`,
              path: `/${e.makeSlug}/${e.modelSlug}/${def.specType}`,
            })),
          ),
        ),
      },
      {
        type: "application/ld+json",
        // Same generator the page renders visibly — FAQPage markup must mirror
        // on-page content or Google flags it as hidden structured data.
        children: JSON.stringify(faqLd(topicFaqs(def, data, answer))),
      },
      {
        type: "application/ld+json",
        // Ranking tables are tabular reference data, same as spec pages, so
        // they declare the same Dataset contract.
        children: JSON.stringify(
          tableDatasetLd({
            name: def.h1,
            path: def.path,
            description,
            keywords: [def.metricLabel, def.specType.replace(/-/g, " "), "vehicle specifications"],
            rowCount: data.entries.length,
            yearMin: Math.min(...data.entries.map((e) => e.year)),
            yearMax: data.latestYear,
            measures: [{ name: def.metricLabel, unitText: def.unit }],
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(speakableLd(url)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: def.h1, path: url },
          ]),
        ),
      },
    ],
  };
}
